import axios from 'axios';
import to from 'await-to-js';
import downloadXlsxFile from '../utils/download-xlsx-file';
import { formatFilters } from '../utils/filters';
import { saveResultsToDatabricks } from '@enums/feature-flags';

const getInitialState = () => {
  return {
    priceOverrides: [],
    downloading: false,
    loading: false,
    count: 0,
    busyImportingPriceOverride: false,
    uploadError: {},
    uploadCounts: {},
  };
};

const store = {
  namespaced: true,

  state: getInitialState(),

  mutations: {
    resetState(state) {
      Object.assign(state, getInitialState());
    },

    setPriceOverrides(state, priceOverrides) {
      state.priceOverrides = priceOverrides;
    },

    setDownloading(state, downloading) {
      state.downloading = downloading;
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

    setBusyImportingPriceOverride(state, importing) {
      state.busyImportingPriceOverride = importing;
    },

    setPriceOverridesCount(state, count) {
      state.count = count;
    },

    setErrorState(state, error) {
      state.uploadError = error;
    },

    setUploadCounts(state, counts) {
      state.uploadCounts = counts;
    },
  },

  actions: {
    async downloadPriceOverrides(
      { rootState, commit },
      { translationMap = {}, where = [], columnFormatters = {} }
    ) {
      commit('setDownloading', true);
      const params = {
        formatForExport: true,
        translationMap,
        where: formatFilters({ where, rootState }),
        // The pick parameter is only required when reading from Mongo and contains fields not
        // present in databricks, so only include it when reading from Mongo.
        ...(!rootState.clientConfig.toggleLogic[saveResultsToDatabricks]
          ? {
              pick: [
                'productKey',
                'productKeyDisplay',
                'productDescription',
                'hierarchy',
                'toolStoreGroupDescription',
                'toolStoreGroupKey',
                'scenarioKey', // required for productToPricesMap
                'overridePrice',
                'optimizedPrice',
              ],
            }
          : {}),
      };
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const [err, response] = await to(
        axios.get(`/api/aggregated-scenario-results/workpackage/${workpackageId}`, {
          params,
        })
      );
      commit('setDownloading', false);
      if (err) throw new Error(err.message);
      const { numberFormats, i18nconfig, exportConfigs } = rootState.clientConfig;
      const { overrideNumberFormat } = i18nconfig;
      const { rowsPerFile } = exportConfigs.exportToExcel;
      downloadXlsxFile(response.data, 'price_overrides.xlsx', {
        rowsPerFile,
        columnFormatters,
        numberFormatsConfig: numberFormats[overrideNumberFormat],
      });
    },

    resetState({ commit }) {
      commit('resetState');
    },

    async uploadPriceOverrides(
      { rootState, commit },
      { dataFile, params = {}, translationMap = {} }
    ) {
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      params.pick = [
        'productKey',
        'productKeyDisplay',
        'productDescription',
        'hierarchy',
        'toolStoreGroupDescription',
        'toolStoreGroupKey',
        'overridePrice',
      ];
      const patchParams = {
        translationMap,
      };
      const [err, res] = await to(
        axios.patch(
          `/api/aggregated-scenario-results/workpackage/${workpackageId}/import-price-overrides`,
          dataFile,
          {
            params: patchParams,
            headers: {
              // Now axios 1.x set the Content-Type to 'application/json' automatically so in order to have the value as 'FormData/HTMLForm' we need to set as undefined
              // You can read more about in the axios migration guide to v1.x on https://github.com/bmuenzenmeyer/axios-1.0.0-migration-guide?tab=readme-ov-file#multipart-form-data-is-no-longer-automatically-set
              'Content-Type': undefined,
            },
          }
        )
      );
      if (err) throw err.response.data;

      if (res.data.error) {
        commit('setErrorState', res.data.error);
      } else {
        commit('setUploadCounts', res.data.counts);
      }
    },

    async uploadPriceOverridesConfirm({ rootState, commit }) {
      commit('setBusyImportingPriceOverride', true);
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const [err] = await to(
        axios.patch(
          `/api/aggregated-scenario-results/workpackage/${workpackageId}/import-price-overrides/confirm`
        )
      );
      commit('setBusyImportingPriceOverride', false);
      if (err) throw new Error(err.message);
      // eslint-disable-next-line
      this._vm.$eventBus.$emit('inputsUploaded');
      // Set temporary flag for unsaved KPI results
      commit('gridView/setTemporaryUnsavedKpiResults', true, { root: true });
    },
  },
};

export default store;
